import { useEffect, useState } from "react";
import { ClipboardCheck, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { api, errMsg, runJob } from "../lib/api";
import { MarkingResults } from "../components/MarkingResults";

export default function Examiner() {
  const [papers, setPapers] = useState([]);
  const [paperId, setPaperId] = useState("");
  const [paper, setPaper] = useState(null);
  const [answers, setAnswers] = useState({});
  const [rawText, setRawText] = useState("");
  const [mode, setMode] = useState("paper");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/papers").then((r) => setPapers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!paperId) return setPaper(null);
    api.get(`/papers/${paperId}`).then((r) => { setPaper(r.data); setAnswers({}); }).catch(() => {});
  }, [paperId]);

  const submit = async () => {
    setLoading(true);
    setResult(null);
    try {
      const payload = mode === "paper"
        ? { paper_id: paperId, answers: Object.entries(answers).map(([n, a]) => ({ number: parseInt(n), answer: a })) }
        : { raw_text: rawText, subject: "Psychology" };
      const job = await runJob(() => api.post("/examiner/mark", payload));
      const { data } = await api.get(`/examiner/submissions/${job.result_id}`);
      setResult(data);
      toast.success("Marking complete!");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="examiner-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">AI Examiner</p>
      <h1 className="text-4xl font-bold tracking-tighter">Mark My Paper</h1>
      <p className="mt-3 text-muted-foreground max-w-2xl">Answer a generated paper or paste your work — a senior AI examiner marks it against the mark scheme, and your grade is looked up from official AQA grade boundaries.</p>

      <Tabs value={mode} onValueChange={setMode} className="mt-8">
        <TabsList className="rounded-full">
          <TabsTrigger value="paper" data-testid="mode-paper-tab" className="rounded-full">Answer a Generated Paper</TabsTrigger>
          <TabsTrigger value="paste" data-testid="mode-paste-tab" className="rounded-full">Paste My Work</TabsTrigger>
        </TabsList>

        <TabsContent value="paper" className="mt-6 space-y-5">
          <Select value={paperId} onValueChange={setPaperId}>
            <SelectTrigger className="w-full md:w-96" data-testid="examiner-paper-select"><SelectValue placeholder="Choose one of your saved papers" /></SelectTrigger>
            <SelectContent>
              {papers.map((p) => <SelectItem key={p.id} value={p.id}>{p.title} — {p.subject} P{p.paper_number}</SelectItem>)}
            </SelectContent>
          </Select>
          {papers.length === 0 && <p className="text-sm text-muted-foreground">No saved papers yet — generate one first.</p>}
          {paper && (
            <div className="space-y-5">
              {paper.questions.map((q) => (
                <div key={q.number} className="rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold font-display text-sm">Question {q.number}</span>
                    <Badge className="bg-primary/15 text-primary hover:bg-primary/15 text-xs">{q.marks} marks</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3 whitespace-pre-line">{q.text}</p>
                  <Textarea data-testid={`answer-input-${q.number}`} rows={3} placeholder="Type your answer…"
                    value={answers[q.number] || ""} onChange={(e) => setAnswers({ ...answers, [q.number]: e.target.value })} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="paste" className="mt-6">
          <Textarea data-testid="raw-text-input" rows={12} placeholder={"Paste your questions and answers, e.g.\n\nQ1: Outline two types of conformity. [4 marks]\nMy answer: Internalisation is when..."}
            value={rawText} onChange={(e) => setRawText(e.target.value)} className="rounded-2xl" />
        </TabsContent>
      </Tabs>

      <Button className="mt-6 rounded-full glow-btn" data-testid="mark-answers-btn" disabled={loading || (mode === "paper" ? !paperId : !rawText.trim())} onClick={submit}>
        {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Marking (30–90s)…</> : <><ClipboardCheck className="h-4 w-4 mr-2" />Mark My Answers</>}
      </Button>

      {result && (
        <div className="mt-10">
          <MarkingResults result={result} />
        </div>
      )}
    </div>
  );
}
